import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from './config-injection';
import { AppConfig } from '../../_api';

@Injectable({
  providedIn: 'root',
})
export class PasswordService {
  private passwordRequirements;

  constructor(@Inject(APP_CONFIG) private config: AppConfig) {
    this.passwordRequirements = config.passwordComplexityOptions || {
      requiredLength: 8,
      requiredUniqueChars: 4,
      requireDigit: true,
      requireLowercase: true,
      requireUppercase: true,
      requireNonAlphanumeric: true,
    };
  }

  getRequirements() {
    return { ...this.passwordRequirements };
  }

  generatePassword(): string {
    // Ensure password meets all requirements
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    // Only use special characters from number keys on US keyboard (shift + 1-0, plus minus/underscore)
    const special = '!@#$%^&*()_-';

    const req = this.passwordRequirements;
    const password: string[] = [];

    // Start with one character from each required category
    if (req.requireUppercase) {
      password.push(uppercase[Math.floor(Math.random() * uppercase.length)]);
    }
    if (req.requireLowercase) {
      password.push(lowercase[Math.floor(Math.random() * lowercase.length)]);
    }
    if (req.requireDigit) {
      password.push(digits[Math.floor(Math.random() * digits.length)]);
    }
    if (req.requireNonAlphanumeric) {
      password.push(special[Math.floor(Math.random() * special.length)]);
    }

    // Build the character set based on requirements
    let allChars = '';
    if (req.requireUppercase || !req.requireUppercase) allChars += uppercase;
    if (req.requireLowercase || !req.requireLowercase) allChars += lowercase;
    if (req.requireDigit || !req.requireDigit) allChars += digits;
    if (req.requireNonAlphanumeric || !req.requireNonAlphanumeric)
      allChars += special;

    // If no specific requirements, use all character types
    if (allChars === '') {
      allChars = uppercase + lowercase + digits + special;
    }

    // Use a longer password to ensure we meet unique character requirements
    const targetLength = Math.max(12, req.requiredLength);

    // Fill the rest with random characters from all categories
    for (let i = password.length; i < targetLength; i++) {
      password.push(allChars[Math.floor(Math.random() * allChars.length)]);
    }

    // Shuffle the password array to avoid predictable patterns
    for (let i = password.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [password[i], password[j]] = [password[j], password[i]];
    }

    return password.join('');
  }

  validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!password) {
      errors.push('Password cannot be empty.');
      return { valid: false, errors };
    }

    const req = this.passwordRequirements;

    if (password.length < req.requiredLength) {
      errors.push(
        `Password must be at least ${req.requiredLength} characters long.`
      );
    }

    if (req.requireDigit && !password.match(/[0-9]/)) {
      errors.push('Password must contain at least one numeric digit.');
    }

    if (req.requireLowercase && !password.match(/[a-z]/)) {
      errors.push('Password must contain at least one lowercase letter.');
    }

    if (req.requireUppercase && !password.match(/[A-Z]/)) {
      errors.push('Password must contain at least one uppercase letter.');
    }

    if (req.requireNonAlphanumeric && !password.match(/[^a-zA-Z0-9]/)) {
      errors.push(
        'Password must contain at least one non-alphanumeric character.'
      );
    }

    const uniqueChars = new Set(password).size;
    if (uniqueChars < req.requiredUniqueChars) {
      errors.push(
        `Password must contain at least ${req.requiredUniqueChars} unique characters.`
      );
    }

    return { valid: errors.length === 0, errors };
  }
}
